// @/components/reusable/StoreStatusProvider.jsx

/**
 * Store Status Provider Component
 * 
 * Context provider that tracks the initialization status of application stores
 * and provides this information to consuming components. Helps with showing
 * appropriate loading states and error handling.
 * 
 * Features:
 * - Tracks initialization of app and content stores
 * - Provides consolidated loading state for the application
 * - Detects page reloads for optimized initialization
 * - Handles store initialization errors
 * - Provides detailed status information via context
 * - Auto-recovery during page reloads with cached data
 * - Session timeout handling for authentication
 * - Memory leak prevention with proper cleanup
 * 
 * @module components/reusable/StoreStatusProvider
 */

import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { isPageReload, markSessionActive, getTimeSinceInit, getSessionState } from "@/utils/session-utils";

// Create context for store status
const StoreStatusContext = createContext({
  appStoreReady: false,
  contentStoreReady: false,
  isLoading: true,
  isReload: false,
  initTime: null,
  hasError: false,
  errorReason: null
});

/**
 * Provider component that tracks store initialization status
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {React.ReactNode} The provider component with children
 */
export const StoreStatusProvider = ({ children }) => {
  // Enhanced status state with error tracking
  const [status, setStatus] = useState({
    appStoreReady: false,
    contentStoreReady: false,
    isLoading: true,
    isReload: false,
    initTime: null,
    hasError: false,
    errorReason: null
  });

  // Use ref to track event listeners and prevent memory leaks
  const cleanupRef = useRef([]);

  useEffect(() => {
    // Check if this is a reload
    const reload = isPageReload();

    // Mark session as active for future reload detection
    markSessionActive();

    // Set initial status
    setStatus(prev => ({
      ...prev,
      isReload: reload,
    }));

    // Add timeout to automatically consider ready after certain time for reloads
    // This prevents UI from being blocked too long during reloads
    let timeoutId = null;

    // For reloads, assume stores are ready after a timeout
    if (reload) {
      const cachedProjectId = typeof window !== 'undefined' && window.appStore ?
        window.appStore.getProjectId() : null;

      if (cachedProjectId) {
        // If we have a cached projectId, we can assume stores will initialize
        // Give a brief timeout to let stores initialize if they can
        timeoutId = setTimeout(() => {
          setStatus(prev => {
            // Only update if not already updated by actual events
            if (prev.isLoading) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[StoreStatus] Assuming stores ready for reload with cached projectId');
              }
              return {
                ...prev,
                appStoreReady: true,
                contentStoreReady: true,
                isLoading: false,
                initTime: getTimeSinceInit()
              };
            }
            return prev;
          });
        }, 1500);

        // Save for cleanup
        cleanupRef.current.push(() => clearTimeout(timeoutId));
      }
    }

    /**
     * Handle app store ready event
     * @private
     * @param {Event} event - The appStoreReady event
     */
    const handleAppStoreReady = (event) => {
      // Clear any timeout when we get a real event
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setStatus(prev => ({
        ...prev,
        appStoreReady: true,
        hasError: event.detail?.success === false,
        errorReason: event.detail?.success === false ? 'app-store' : prev.errorReason,
        isLoading: !(prev.contentStoreReady),
      }));
    };

    /**
     * Handle content store ready event
     * @private
     * @param {Event} event - The contentStoreReady event
     */
    const handleContentStoreReady = (event) => {
      // Clear any timeout when we get a real event
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setStatus(prev => ({
        ...prev,
        contentStoreReady: true,
        hasError: event.detail?.success === false,
        errorReason: event.detail?.success === false ? 'content-store' : prev.errorReason,
        isLoading: !(prev.appStoreReady),
      }));
    };

    /**
     * Handle combined stores ready event
     * @private
     * @param {Event} event - The storesReady event
     */
    const handleStoresReady = (event) => {
      // Clear any timeout when we get a real event
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setStatus(prev => ({
        ...prev,
        appStoreReady: true,
        contentStoreReady: true,
        isLoading: false,
        hasError: event.detail?.success === false,
        errorReason: event.detail?.success === false ? 'stores' : prev.errorReason,
        initTime: event.detail?.initTime || getTimeSinceInit(),
      }));
    };

    /**
     * Handle store error event
     * @private
     * @param {Event} event - The storeError event
     */
    const handleStoreError = (event) => {
      setStatus(prev => ({
        ...prev,
        hasError: true,
        errorReason: event.detail?.source || 'unknown',
      }));
    };

    // Register event listeners
    window.addEventListener('appStoreReady', handleAppStoreReady);
    window.addEventListener('contentStoreReady', handleContentStoreReady);
    window.addEventListener('storesReady', handleStoresReady);
    window.addEventListener('storeError', handleStoreError);

    // Save for cleanup
    cleanupRef.current.push(() => window.removeEventListener('appStoreReady', handleAppStoreReady));
    cleanupRef.current.push(() => window.removeEventListener('contentStoreReady', handleContentStoreReady));
    cleanupRef.current.push(() => window.removeEventListener('storesReady', handleStoresReady));
    cleanupRef.current.push(() => window.removeEventListener('storeError', handleStoreError));

    /**
     * Handle session timeout event
     * @private
     */
    const handleSessionTimeout = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[StoreStatus] Detected session timeout, marking stores as not ready');
      }

      setStatus({
        appStoreReady: false,
        contentStoreReady: false,
        isLoading: true,
        isReload: false,
        initTime: null,
        hasError: false,
        errorReason: null
      });
    };

    window.addEventListener('sessionTimeout', handleSessionTimeout);
    cleanupRef.current.push(() => window.removeEventListener('sessionTimeout', handleSessionTimeout));

    // Clean up all listeners on unmount
    return () => {
      // Clean up all registered listeners
      cleanupRef.current.forEach(cleanup => cleanup());
      cleanupRef.current = [];
    };
  }, []);

  /**
   * Get detailed status information including session state
   * @returns {Object} Detailed status information
   */
  const getDetailedStatus = () => {
    const sessionState = getSessionState();

    return {
      ...status,
      session: sessionState
    };
  };

  // Enhanced context value with additional info and functions
  const contextValue = {
    ...status,
    getDetailedStatus,
    // Explicitly check if both stores are ready (safer than just !isLoading)
    isBothStoresReady: status.appStoreReady && status.contentStoreReady
  };

  return (
    <StoreStatusContext.Provider value={contextValue}>
      {children}
    </StoreStatusContext.Provider>
  );
};

/**
 * Hook to access store status context
 * 
 * @returns {Object} Store status context
 * @throws {Error} If used outside of StoreStatusProvider
 */
export const useStoreStatus = () => {
  const context = useContext(StoreStatusContext);

  if (!context) {
    throw new Error('useStoreStatus must be used within a StoreStatusProvider');
  }

  return context;
};

export default StoreStatusProvider;